import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Avatar,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fade,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import VideocamOffRoundedIcon from "@mui/icons-material/VideocamOffRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SparklesIcon from "@mui/icons-material/AutoAwesomeRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { toast } from "react-toastify";
import ToastApp from "../../ToastApp";
import api, { FACE_API } from "../../utils/Url";

interface MemberDetails {
  mem_cd: string;
  name: string;
  departmentName: string;
  mem_degree: string;
  mem_prmntphone: string;
  mem_email: string;
  imageFile?: string;
}

interface RecognitionResult {
  success: boolean;
  recognized: boolean;
  member?: {
    mem_cd: string;
    mem_name: string;
    mem_dept?: string;
    mem_inst?: string;
    mem_degree?: string;
    mem_gender?: string;
  };
  image_path?: string;
}

interface MultiImageItem {
  id: string;
  blob: Blob;
  previewUrl: string;
  source: "camera" | "upload";
}

interface RegisteredFacePicture {
  face_id: number;
  mem_cd: string;
  image_url: string;
  crop_url?: string;
  confidence?: number;
  created_at?: string;
}

export default function FaceRegistration() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const addMoreFileInputRef = useRef<HTMLInputElement | null>(null);

  // Mode: 0 = Enroll (Single Face Registration), 1 = Verify Member & Add/Manage Faces
  const [activeTab, setActiveTab] = useState<number>(0);

  // Video Device States
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");

  // Search & Member States
  const [memberOptions, setMemberOptions] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [timerCheck, setTimerCheck] = useState<NodeJS.Timeout | null>(null);

  // Post-Verification Additional Images Collection
  const [addMoreImages, setAddMoreImages] = useState<MultiImageItem[]>([]);

  // Registered Faces Management State
  const [hasFetchedFaces, setHasFetchedFaces] = useState<boolean>(false);
  const [registeredFaces, setRegisteredFaces] = useState<RegisteredFacePicture[]>([]);
  const [selectedFaceIds, setSelectedFaceIds] = useState<number[]>([]);
  const [loadingFaces, setLoadingFaces] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [previewFace, setPreviewFace] = useState<RegisteredFacePicture | null>(null);

  // Action States
  const [loading, setLoading] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [flashEffect, setFlashEffect] = useState(false);

  // Verification Result
  const [verifyResult, setVerifyResult] = useState<RecognitionResult | null>(null);

  // ----------------- CAMERA CONTROLLER -----------------
  const stopCam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  }, []);

  const resetAllForm = () => {
    setSelectedMember(null);
    setMemberDetails(null);
    setMemberOptions([]);
    setVerifyResult(null);
    setAddMoreImages([]);
    setRegisteredFaces([]);
    setSelectedFaceIds([]);
    setHasFetchedFaces(false);
    setPreviewFace(null);
    stopCam();
  };

  const getVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setVideoDevices(videoInputs);

      if (videoInputs.length > 0) {
        const realCam =
          videoInputs.find(
            (d) =>
              !d.label.toLowerCase().includes("virtual") &&
              !d.label.toLowerCase().includes("eshare")
          ) || videoInputs[0];

        setSelectedDeviceId(realCam.deviceId);
        return realCam.deviceId;
      }
    } catch (e) {
      console.error("Device detection error:", e);
    }
    return "";
  };

  const startCam = useCallback(
    async (deviceId?: string) => {
      setCameraError(null);
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }

        const constraints: MediaStreamConstraints = {
          video: deviceId || selectedDeviceId
            ? { deviceId: { exact: deviceId || selectedDeviceId } }
            : { facingMode: "user" },
          audio: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.muted = true;
          video.setAttribute("playsinline", "true");

          video.onloadeddata = async () => {
            try {
              await video.play();
              setCameraOn(true);
            } catch (err) {
              console.error("Play error:", err);
            }
          };
        }
      } catch (err: any) {
        console.error("Camera Error:", err);
        setCameraOn(false);
        setCameraError(err?.message || "Webcam not available");
        toast.error("Camera Error: " + (err?.message || "Check Permissions"));
      }
    },
    [selectedDeviceId]
  );

  useEffect(() => {
    const initDevices = async () => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        console.log("Permission requested");
      }
      await getVideoDevices();
    };
    initDevices();

    return () => {
      stopCam();
    };
  }, [stopCam]);

  const captureBlob = async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95)
    );
  };

  // ----------------- MEMBER SEARCH -----------------
  const handleInputChange = (event: any) => {
    const query = event?.target?.value || "";
    if (query.trim() !== "") {
      if (timerCheck) clearTimeout(timerCheck);
      const checkResult = setTimeout(() => {
        getApplicableMembers(query);
      }, 300);
      setTimerCheck(checkResult);
    }
  };

  const getApplicableMembers = async (nameData: string) => {
    try {
      setSearchLoading(true);
      const res = await api.get(`AssigningRfid/GetMember?name=${nameData}`);
      if (res.data) {
        const transformedData = res.data.map((item: any) => {
          const name = item.label.replace(/^\(.*?\)/, "").trim();
          return {
            value: item.value,
            label: `${item.value} - ${name}`,
            name: name,
          };
        });
        setMemberOptions(transformedData);
      }
    } catch (error) {
      console.error("Member Search Error:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchMemberFullDetails = async (memCode: string) => {
    if (!memCode) return;
    setLoading(true);
    try {
      const payload = {
        mem_cd: memCode,
        mem_ctgry: "",
        mem_inst: "",
        mem_dept: "",
        mem_degree: "",
        image: true,
        year: 0,
      };
      const res = await api.post(`Member/Getm_member`, payload);
      if (res.data?.data && res.data.data.length > 0) {
        const item = res.data.data[0];
        setMemberDetails({
          mem_cd: item.mem_cd,
          name: [item.mem_firstnm, item.mem_midnm, item.mem_lstnm]
            .filter(Boolean)
            .join(" "),
          departmentName: item.departmentName || "N/A",
          mem_degree: item.mem_degree || "N/A",
          mem_prmntphone: item.mem_prmntphone || "N/A",
          mem_email: item.mem_email || "N/A",
          imageFile: item.imageFile || "",
        });
      }
    } catch (err) {
      toast.error("Failed to load member profile details");
    } finally {
      setLoading(false);
    }
  };

  // ----------------- REGISTER SINGLE FACE -----------------
  const registerSingleFace = async () => {
    if (!memberDetails?.mem_cd) {
      toast.warn("Please select a member first!");
      return;
    }
    if (!cameraOn) {
      toast.warn("Please turn on the camera!");
      return;
    }

    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 250);

    setLoading(true);
    try {
      const blob = await captureBlob();
      if (!blob) {
        toast.error("Failed to capture image from camera.");
        setLoading(false);
        return;
      }

      const form = new FormData();
      form.append("mem_cd", memberDetails.mem_cd);
      form.append("image", blob, "face.jpg");

      const res = await fetch(`${FACE_API}/register/`, {
        method: "POST",
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.message || "Face Registered Successfully! 🎉");
        setActiveTab(1);
        toast.info("Switched to verification mode to verify registered face.");
      } else {
        toast.error(data.detail || data.message || "Registration Failed.");
      }
    } catch (e) {
      toast.error("Server connection error.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------- 📸 GET REGISTERED FACES (ONLY WHEN CLICKED) -----------------
  const fetchRegisteredFaces = async (memCd: string) => {
    if (!memCd) return;
    setLoadingFaces(true);
    setSelectedFaceIds([]);

    try {
      const res = await fetch(`${FACE_API}/register/${encodeURIComponent(memCd)}/pics`);
      if (res.ok) {
        const data = await res.json();
        const pictures: RegisteredFacePicture[] = data.pictures || [];
        setRegisteredFaces(pictures);
        setHasFetchedFaces(true);

        if (pictures.length > 0) {
          toast.success(`Loaded ${pictures.length} registered face(s)! 📸`);
        } else {
          toast.info("No registered face photos found for this member.");
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to fetch member face pictures.");
      }
    } catch (e) {
      console.error("Fetch face pictures error:", e);
      toast.error("Network error while fetching registered faces.");
    } finally {
      setLoadingFaces(false);
    }
  };

  // ----------------- 🗑️ DELETE MULTIPLE FACES -----------------
  const deleteSelectedFaces = async () => {
    const memCd = verifyResult?.member?.mem_cd;
    if (!memCd) {
      toast.warn("No verified member selected!");
      return;
    }
    if (selectedFaceIds.length === 0) {
      toast.warn("Please select at least one face picture to delete!");
      return;
    }

    setDeleteLoading(true);
    setOpenDeleteDialog(false);

    let successCount = 0;
    let failCount = 0;

    try {
      for (const faceId of selectedFaceIds) {
        const deleteUrl = `${FACE_API}/register/${encodeURIComponent(memCd)}/pics/${faceId}`;
        const res = await fetch(deleteUrl, {
          method: "DELETE",
        });

        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Deleted ${successCount} face picture(s) successfully! 🗑️`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} face picture(s).`);
      }

      // Re-fetch updated list
      await fetchRegisteredFaces(memCd);
      setSelectedFaceIds([]);
    } catch (e) {
      console.error(e);
      toast.error("Error occurred while deleting face photos.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleFaceSelect = (faceId: number) => {
    setSelectedFaceIds((prev) =>
      prev.includes(faceId) ? prev.filter((id) => id !== faceId) : [...prev, faceId]
    );
  };

  const handleSelectAllFaces = () => {
    if (selectedFaceIds.length === registeredFaces.length) {
      setSelectedFaceIds([]);
    } else {
      setSelectedFaceIds(registeredFaces.map((f) => f.face_id));
    }
  };

  // ----------------- ➕ ADD EXTRA FACE ANGLES -----------------
  const snapAddMorePhoto = async () => {
    if (!cameraOn) {
      toast.warn("Please turn on the camera first!");
      return;
    }

    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 200);

    const blob = await captureBlob();
    if (!blob) {
      toast.error("Failed to capture frame.");
      return;
    }

    const newItem: MultiImageItem = {
      id: Math.random().toString(36).substring(7),
      blob: blob,
      previewUrl: URL.createObjectURL(blob),
      source: "camera",
    };

    setAddMoreImages((prev) => [...prev, newItem]);
    toast.success(`Captured Angle #${addMoreImages.length + 1} 📸`);
  };

  const handleAddMoreFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArr = Array.from(e.target.files);
      const newItems: MultiImageItem[] = filesArr.map((file) => ({
        id: Math.random().toString(36).substring(7),
        blob: file,
        previewUrl: URL.createObjectURL(file),
        source: "upload",
      }));

      setAddMoreImages((prev) => [...prev, ...newItems]);
      toast.success(`Added ${newItems.length} photos 📁`);
    }
  };

  const saveExtraFacesAfterVerify = async () => {
    const memCd = verifyResult?.member?.mem_cd;
    if (!memCd) {
      toast.warn("No verified member selected!");
      return;
    }

    let imagesToUpload = [...addMoreImages];

    if (imagesToUpload.length === 0) {
      if (!cameraOn) {
        toast.warn("Please snap photos or choose files to add!");
        return;
      }
      const blob = await captureBlob();
      if (!blob) return;
      imagesToUpload = [
        { id: "direct", blob: blob, previewUrl: URL.createObjectURL(blob), source: "camera" },
      ];
    }

    setLoading(true);
    let count = 0;

    try {
      for (let i = 0; i < imagesToUpload.length; i++) {
        const item = imagesToUpload[i];
        const form = new FormData();
        form.append("mem_cd", memCd);
        form.append("image", item.blob, `extra_face_${i + 1}.jpg`);

        const res = await fetch(`${FACE_API}/register/add-face`, {
          method: "POST",
          body: form,
        });

        if (res.ok) count++;
      }

      if (count > 0) {
        toast.success(`Added ${count} new face angle(s) successfully! ✨`);
        setAddMoreImages([]);
        // Re-fetch only if user has already opened registered faces
        if (hasFetchedFaces) {
          fetchRegisteredFaces(memCd);
        }
      } else {
        toast.error("Failed to add new face angles.");
      }
    } catch (e) {
      toast.error("Connection error while adding faces.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------- API: VERIFY FACE (/recognize/) -----------------
  const verifyFace = async () => {
    if (!cameraOn) {
      toast.warn("Please turn on the camera to verify face!");
      return;
    }

    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 200);

    setLoading(true);
    setVerifyResult(null);
    setAddMoreImages([]);
    setRegisteredFaces([]);
    setSelectedFaceIds([]);
    setHasFetchedFaces(false); // Reset so it only displays when clicked!

    try {
      const blob = await captureBlob();
      if (!blob) {
        toast.error("Failed to capture image for recognition.");
        setLoading(false);
        return;
      }

      const form = new FormData();
      form.append("image", blob, "recognize.jpg");

      const res = await fetch(`${FACE_API}/recognize/`, {
        method: "POST",
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.recognized) {
        setVerifyResult(data);
        toast.success(`Recognized: ${data.member?.mem_name || "Member Verified"}`);
      } else {
        setVerifyResult(data);
        toast.error(data.message || data.detail || "No matching face found!");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error recognizing face.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: "1550px",
        margin: "0 auto",
        background: "linear-gradient(180deg, #f0fdf4 0%, #f8fafc 100%)",
        minHeight: "100vh",
      }}
    >
      <ToastApp />

      <style>
        {`
          @keyframes scanBeam {
            0% { top: 6%; opacity: 0.9; }
            50% { top: 88%; opacity: 1; }
            100% { top: 6%; opacity: 0.9; }
          }
          @keyframes pulseGlow {
            0% { transform: scale(0.98); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
            70% { transform: scale(1); box-shadow: 0 0 0 12px rgba(16, 185, 129, 0); }
            100% { transform: scale(0.98); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
          }
        `}
      </style>

      {/* TOP HERO BANNER */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          borderRadius: 4,
          background:
            activeTab === 0
              ? "linear-gradient(135deg, #059669 0%, #0d9488 50%, #0284c7 100%)"
              : "linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)",
          color: "#fff",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 44, height: 44 }}>
              <SparklesIcon sx={{ color: "#fef08a", fontSize: 26 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={900} letterSpacing="-0.5px">
                AI Face Biometric System
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Single-Shot Registration, Live Verification & Multi-Face Management
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* TABS */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(8px)",
            borderRadius: 3,
            p: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => {
              setActiveTab(val);
              setVerifyResult(null);
              setRegisteredFaces([]);
              setSelectedFaceIds([]);
              setHasFetchedFaces(false);
            }}
            TabIndicatorProps={{ sx: { bgcolor: "#fff", height: 3, borderRadius: 2 } }}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(255,255,255,0.7)",
                fontWeight: 800,
                textTransform: "none",
                fontSize: "14px",
                "&.Mui-selected": { color: "#ffffff" },
              },
            }}
          >
            <Tab icon={<HowToRegRoundedIcon />} iconPosition="start" label="Register Face" />
            <Tab icon={<VerifiedUserRoundedIcon />} iconPosition="start" label="Verify & Manage Faces" />
          </Tabs>
        </Paper>
      </Paper>

      {/* SEARCH BAR & DEVICE SELECTOR */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3.5,
          backgroundColor: "#ffffff",
          border: "2px solid #e0f2fe",
          boxShadow: "0 4px 15px rgba(2, 132, 199, 0.06)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {activeTab === 0 ? (
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={memberOptions}
                getOptionLabel={(option: any) => option?.label || ""}
                loading={searchLoading}
                value={selectedMember}
                onInputChange={(_, val) => handleInputChange({ target: { value: val } })}
                onChange={(_, newValue) => {
                  setSelectedMember(newValue);
                  if (newValue?.value) {
                    fetchMemberFullDetails(newValue.value);
                  } else {
                    resetAllForm();
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search Member by Name or Member Code"
                    placeholder="e.g. Type Parul or CCAPCC240012..."
                    InputProps={{
                      ...params.InputProps,
                      sx: { borderRadius: 3, fontSize: "15px", bgcolor: "#f8fafc" },
                      startAdornment: <SearchRoundedIcon sx={{ color: "#0284c7", mr: 1 }} />,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress color="primary" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" fontWeight={800} color="#1e40af">
                🔍 Face Verification & Multi-Face Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Turn on camera and click <b>"Verify Face"</b>. Once recognized, click <b>"Get Faces"</b> to view or delete them.
              </Typography>
            </Grid>
          )}

          {/* Camera Selection */}
          <Grid item xs={12} md={4}>
            {videoDevices.length > 1 ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Select Camera Device</InputLabel>
                <Select
                  value={selectedDeviceId}
                  label="Select Camera Device"
                  onChange={(e) => {
                    setSelectedDeviceId(e.target.value);
                    if (cameraOn) {
                      startCam(e.target.value);
                    }
                  }}
                  sx={{ borderRadius: 3, bgcolor: "#f8fafc" }}
                >
                  {videoDevices.map((device, index) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" fontWeight={700} color="#059669" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleRoundedIcon fontSize="small" /> Default Camera Ready
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* MAIN WORKSPACE */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN: Member Details or Verification Results */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
              backgroundColor: "#ffffff",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 80,
                background:
                  activeTab === 0
                    ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                    : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              }}
            />
            <CardContent sx={{ p: 3, mt: -6 }}>
              {/* 1. ENROLLMENT VIEW */}
              {activeTab === 0 && (
                memberDetails ? (
                  <Fade in={true}>
                    <Box>
                      <Stack direction="row" spacing={2.5} alignItems="flex-end" mb={3}>
                        <Avatar
                          src={memberDetails.imageFile ? `data:image/jpeg;base64,${memberDetails.imageFile}` : ""}
                          sx={{
                            width: 86,
                            height: 86,
                            border: "4px solid #ffffff",
                            boxShadow: "0 8px 20px rgba(37, 99, 235, 0.2)",
                            backgroundColor: "#eff6ff",
                            color: "#3b82f6",
                          }}
                        >
                          <PersonRoundedIcon sx={{ fontSize: 50 }} />
                        </Avatar>

                        <Box sx={{ pb: 0.5 }}>
                          <Typography variant="h6" fontWeight={900} color="#0f172a" lineHeight={1.2}>
                            {memberDetails.name}
                          </Typography>
                          <Chip
                            label={memberDetails.mem_cd}
                            size="small"
                            sx={{
                              mt: 0.8,
                              fontWeight: 800,
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      </Stack>

                      <Stack spacing={1.5}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 2 }}>
                          <ApartmentRoundedIcon sx={{ color: "#16a34a", fontSize: 24 }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 700 }}>DEPARTMENT</Typography>
                            <Typography variant="body2" fontWeight={800} color="#0f172a">{memberDetails.departmentName}</Typography>
                          </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, backgroundColor: "#f5f3ff", border: "1px solid #ddd6fe", display: "flex", alignItems: "center", gap: 2 }}>
                          <SchoolRoundedIcon sx={{ color: "#7c3aed", fontSize: 24 }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: "#6d28d9", fontWeight: 700 }}>COURSE / DEGREE</Typography>
                            <Typography variant="body2" fontWeight={800} color="#0f172a">{memberDetails.mem_degree}</Typography>
                          </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", gap: 2 }}>
                          <PhoneIphoneRoundedIcon sx={{ color: "#2563eb", fontSize: 24 }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: "#1d4ed8", fontWeight: 700 }}>MOBILE NUMBER</Typography>
                            <Typography variant="body2" fontWeight={800} color="#0f172a">{memberDetails.mem_prmntphone}</Typography>
                          </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, backgroundColor: "#fffbeb", border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 2 }}>
                          <EmailRoundedIcon sx={{ color: "#d97706", fontSize: 24 }} />
                          <Box sx={{ overflow: "hidden" }}>
                            <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 700 }}>EMAIL ADDRESS</Typography>
                            <Typography variant="body2" fontWeight={800} color="#0f172a" noWrap>{memberDetails.mem_email}</Typography>
                          </Box>
                        </Paper>
                      </Stack>
                    </Box>
                  </Fade>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Avatar sx={{ width: 70, height: 70, bgcolor: "#f1f5f9", color: "#94a3b8", margin: "0 auto 16px" }}>
                      <PersonRoundedIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={800} color="#475569">Ready for Enrollment</Typography>
                    <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>Search a member above to register their face.</Typography>
                  </Box>
                )
              )}

              {/* 2. VERIFICATION & REGISTERED FACES VIEW */}
              {activeTab === 1 && (
                verifyResult ? (
                  <Fade in={true}>
                    <Box>
                      {verifyResult.recognized ? (
                        <>
                          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <Avatar sx={{ width: 64, height: 64, bgcolor: "#dcfce7", color: "#16a34a" }}>
                              <CheckCircleRoundedIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Chip label="VERIFIED MEMBER" color="success" size="small" sx={{ fontWeight: 800, mb: 0.5 }} />
                              <Typography variant="h6" fontWeight={900} color="#0f172a">
                                {verifyResult.member?.mem_name || "Verified Member"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Member Code: <b>{verifyResult.member?.mem_cd}</b>
                              </Typography>
                            </Box>
                          </Stack>

                          {/* Member Info Cards */}
                          <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                              <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>DEPARTMENT</Typography>
                                <Typography variant="body2" fontWeight={800} color="#1e293b" noWrap>
                                  {verifyResult.member?.mem_dept || "N/A"}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>DEGREE / CATEGORY</Typography>
                                <Typography variant="body2" fontWeight={800} color="#1e293b" noWrap>
                                  {verifyResult.member?.mem_degree || "N/A"}
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          {/* ========================================================
                              SECTION 1: REGISTERED FACES (GET, VIEW & MULTI-DELETE)
                             ======================================================== */}
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              mb: 2.5,
                              bgcolor: "#f8fafc",
                              borderRadius: 3,
                              border: "1.5px solid #e2e8f0",
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CollectionsRoundedIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight={900} color="#1e293b">
                                  Registered Faces {hasFetchedFaces ? `(${registeredFaces.length})` : ""}
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={loadingFaces ? <CircularProgress size={12} color="inherit" /> : <RefreshRoundedIcon />}
                                  onClick={() => verifyResult.member?.mem_cd && fetchRegisteredFaces(verifyResult.member.mem_cd)}
                                  disabled={loadingFaces}
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 800,
                                    textTransform: "none",
                                    py: 0.4,
                                    px: 1.8,
                                    borderRadius: 2,
                                    bgcolor: "#2563eb",
                                  }}
                                >
                                  {loadingFaces ? "Fetching..." : "Get Faces"}
                                </Button>

                                {hasFetchedFaces && registeredFaces.length > 0 && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleSelectAllFaces}
                                    sx={{ fontSize: "11px", fontWeight: 700, textTransform: "none", py: 0.3 }}
                                  >
                                    {selectedFaceIds.length === registeredFaces.length ? "Deselect All" : "Select All"}
                                  </Button>
                                )}
                              </Stack>
                            </Stack>

                            {/* Registered Faces Content */}
                            {loadingFaces ? (
                              <Box sx={{ textAlign: "center", py: 3 }}>
                                <CircularProgress size={26} sx={{ color: "#2563eb" }} />
                                <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                                  Fetching member photos from server...
                                </Typography>
                              </Box>
                            ) : !hasFetchedFaces ? (
                              // Initial state before clicking "Get Faces"
                              <Paper
                                elevation={0}
                                sx={{
                                  textAlign: "center",
                                  py: 3,
                                  px: 2,
                                  bgcolor: "#ffffff",
                                  border: "1px dashed #cbd5e1",
                                  borderRadius: 2.5,
                                }}
                              >
                                <CollectionsRoundedIcon sx={{ fontSize: 32, color: "#94a3b8", mb: 0.5 }} />
                                <Typography variant="body2" fontWeight={700} color="#475569">
                                  Registered faces are not loaded yet.
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Click the <b>"Get Faces"</b> button above to view and manage this member's saved photos.
                                </Typography>
                              </Paper>
                            ) : registeredFaces.length > 0 ? (
                              <Box>
                                <Grid container spacing={1.5}>
                                  {registeredFaces.map((face) => {
                                    const isSelected = selectedFaceIds.includes(face.face_id);
                                    const displayImg = face.crop_url || face.image_url;

                                    return (
                                      <Grid item xs={4} sm={3} key={face.face_id}>
                                        <Box
                                          sx={{
                                            position: "relative",
                                            borderRadius: 2.5,
                                            overflow: "hidden",
                                            border: isSelected ? "3px solid #ef4444" : "2px solid #cbd5e1",
                                            boxShadow: isSelected ? "0 4px 12px rgba(239,68,68,0.3)" : "none",
                                            transition: "all 0.2s ease-in-out",
                                            backgroundColor: "#0f172a",
                                            height: 90,
                                          }}
                                        >
                                          {/* Click image to toggle selection */}
                                          <Avatar
                                            variant="rounded"
                                            src={displayImg}
                                            onClick={() => handleToggleFaceSelect(face.face_id)}
                                            sx={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <PersonRoundedIcon />
                                          </Avatar>

                                          {/* Selection Checkbox Overlay */}
                                          <Box
                                            onClick={() => handleToggleFaceSelect(face.face_id)}
                                            sx={{
                                              position: "absolute",
                                              top: 3,
                                              left: 3,
                                              backgroundColor: isSelected ? "rgba(239,68,68,0.95)" : "rgba(15,23,42,0.65)",
                                              borderRadius: 1.5,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0.2,
                                              cursor: "pointer",
                                              zIndex: 2,
                                            }}
                                          >
                                            {isSelected ? (
                                              <CheckBoxRoundedIcon sx={{ fontSize: 16, color: "#ffffff" }} />
                                            ) : (
                                              <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 16, color: "#ffffff" }} />
                                            )}
                                          </Box>

                                          {/* 👁️ VIEW FULL PHOTO BUTTON */}
                                          <Tooltip title="View Face Details" arrow>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewFace(face);
                                              }}
                                              sx={{
                                                position: "absolute",
                                                top: 3,
                                                right: 3,
                                                backgroundColor: "rgba(15,23,42,0.75)",
                                                color: "#ffffff",
                                                p: "3px",
                                                zIndex: 2,
                                                "&:hover": {
                                                  backgroundColor: "#2563eb",
                                                },
                                              }}
                                            >
                                              <VisibilityRoundedIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </Tooltip>

                                          {/* Face ID Tag */}
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              bottom: 0,
                                              left: 0,
                                              right: 0,
                                              backgroundColor: "rgba(15,23,42,0.85)",
                                              color: "#ffffff",
                                              fontSize: "9px",
                                              fontWeight: 800,
                                              textAlign: "center",
                                              py: 0.2,
                                              letterSpacing: "0.5px",
                                            }}
                                          >
                                            ID: #{face.face_id}
                                          </Box>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>

                                {/* Delete Selection Action Bar */}
                                {selectedFaceIds.length > 0 && (
                                  <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="caption" fontWeight={800} color="#b91c1c">
                                      {selectedFaceIds.length} face photo(s) selected
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="error"
                                      startIcon={deleteLoading ? <CircularProgress size={14} color="inherit" /> : <DeleteForeverRoundedIcon />}
                                      onClick={() => setOpenDeleteDialog(true)}
                                      disabled={deleteLoading}
                                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, fontSize: "12px" }}
                                    >
                                      Delete Selected ({selectedFaceIds.length})
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ textAlign: "center", py: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  No registered face photos found on server for this member.
                                </Typography>
                              </Box>
                            )}
                          </Paper>

                          {/* ========================================================
                              SECTION 2: ADD MULTIPLE ANGLES POST-VERIFICATION
                             ======================================================== */}
                          <Paper elevation={0} sx={{ p: 2, bgcolor: "#f5f3ff", borderRadius: 3, border: "1px solid #ddd6fe" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" fontWeight={800} color="#6d28d9">
                                + Add More Face Angles ({addMoreImages.length})
                              </Typography>

                              <Stack direction="row" spacing={1}>
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  ref={addMoreFileInputRef}
                                  style={{ display: "none" }}
                                  onChange={handleAddMoreFiles}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<UploadFileRoundedIcon />}
                                  onClick={() => addMoreFileInputRef.current?.click()}
                                  sx={{ fontSize: "11px", fontWeight: 800, textTransform: "none", py: 0.3 }}
                                >
                                  Upload Files
                                </Button>

                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  disabled={!cameraOn}
                                  startIcon={<CameraAltRoundedIcon />}
                                  onClick={snapAddMorePhoto}
                                  sx={{ fontSize: "11px", fontWeight: 800, textTransform: "none", py: 0.3, bgcolor: "#7c3aed" }}
                                >
                                  + Snap Angle
                                </Button>
                              </Stack>
                            </Stack>

                            {addMoreImages.length > 0 && (
                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                {addMoreImages.map((img) => (
                                  <Grid item xs={3} key={img.id}>
                                    <Box sx={{ position: "relative" }}>
                                      <Avatar
                                        src={img.previewUrl}
                                        variant="rounded"
                                        sx={{ width: "100%", height: 50, border: "2px solid #7c3aed" }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => setAddMoreImages((prev) => prev.filter((i) => i.id !== img.id))}
                                        sx={{ position: "absolute", top: -5, right: -5, bgcolor: "#ef4444", color: "#fff", p: "2px" }}
                                      >
                                        <CloseRoundedIcon sx={{ fontSize: 12 }} />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            )}

                            <Button
                              fullWidth
                              variant="contained"
                              color="secondary"
                              disabled={loading || addMoreImages.length === 0}
                              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighRoundedIcon />}
                              onClick={saveExtraFacesAfterVerify}
                              sx={{ mt: 2, borderRadius: 2, fontWeight: 800, textTransform: "none", bgcolor: "#7c3aed" }}
                            >
                              {loading ? "Adding Embeddings..." : `Save (${addMoreImages.length}) New Face Angles`}
                            </Button>
                          </Paper>
                        </>
                      ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Avatar sx={{ width: 64, height: 64, bgcolor: "#fee2e2", color: "#dc2626", margin: "0 auto 12px" }}>
                            <CancelRoundedIcon sx={{ fontSize: 40 }} />
                          </Avatar>
                          <Typography variant="h6" fontWeight={800} color="#991b1b">
                            Not Recognized
                          </Typography>
                          <Typography variant="body2" color="#b91c1c" sx={{ mt: 0.5 }}>
                            No registered biometric record matched this face.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Fade>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Avatar sx={{ width: 70, height: 70, bgcolor: "#e0e7ff", color: "#4f46e5", margin: "0 auto 16px" }}>
                      <VerifiedUserRoundedIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={800} color="#3730a3">
                      Face Recognition Ready
                    </Typography>
                    <Typography variant="body2" color="#6b7280" sx={{ mt: 0.5 }}>
                      Click <b>"Verify Face"</b> to run live identification and view/manage saved faces.
                    </Typography>
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT COLUMN: Camera Viewport */}
        <Grid item xs={12} lg={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CenterFocusStrongRoundedIcon sx={{ color: activeTab === 0 ? "#059669" : "#4f46e5" }} />
                <Typography variant="subtitle1" fontWeight={900} color="#0f172a">
                  Optical Scanner Viewport
                </Typography>
              </Stack>
              <Chip
                label={cameraOn ? "● OPTICAL FEED ACTIVE" : "CAMERA OFFLINE"}
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: "11px",
                  backgroundColor: cameraOn ? "#ecfdf5" : "#f1f5f9",
                  color: cameraOn ? "#059669" : "#64748b",
                  border: `1px solid ${cameraOn ? "#a7f3d0" : "#e2e8f0"}`,
                }}
              />
            </Stack>

            {/* Video Viewport Container */}
            <Box
              sx={{
                width: "100%",
                height: 380,
                backgroundColor: "#090d16",
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "inset 0 0 30px rgba(0,0,0,0.8)",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: "scaleX(-1)",
                  display: cameraOn ? "block" : "none",
                  zIndex: 1,
                }}
              />

              {flashEffect && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "#ffffff",
                    zIndex: 10,
                    opacity: 0.9,
                    transition: "opacity 0.2s ease-out",
                  }}
                />
              )}

              {cameraOn && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      left: "6%",
                      right: "6%",
                      height: "3px",
                      background:
                        activeTab === 0
                          ? "linear-gradient(90deg, transparent, #10b981, #38bdf8, transparent)"
                          : "linear-gradient(90deg, transparent, #6366f1, #a855f7, transparent)",
                      boxShadow: activeTab === 0 ? "0 0 12px #10b981" : "0 0 12px #6366f1",
                      animation: "scanBeam 2.2s infinite ease-in-out",
                      zIndex: 3,
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      width: 170,
                      height: 230,
                      borderRadius: "50%",
                      border: `2.5px dashed ${activeTab === 0 ? "#38bdf8" : "#818cf8"}`,
                      animation: "pulseGlow 2.5s infinite",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 3,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#fff",
                        backgroundColor: "rgba(15, 23, 42, 0.7)",
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 2,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "1px",
                      }}
                    >
                      ALIGN FACE HERE
                    </Typography>
                  </Box>

                  <Box sx={{ position: "absolute", top: 16, left: 16, width: 22, height: 22, borderTop: "3px solid #10b981", borderLeft: "3px solid #10b981", zIndex: 3 }} />
                  <Box sx={{ position: "absolute", top: 16, right: 16, width: 22, height: 22, borderTop: "3px solid #10b981", borderRight: "3px solid #10b981", zIndex: 3 }} />
                  <Box sx={{ position: "absolute", bottom: 16, left: 16, width: 22, height: 22, borderBottom: "3px solid #10b981", borderLeft: "3px solid #10b981", zIndex: 3 }} />
                  <Box sx={{ position: "absolute", bottom: 16, right: 16, width: 22, height: 22, borderBottom: "3px solid #10b981", borderRight: "3px solid #10b981", zIndex: 3 }} />
                </>
              )}

              {!cameraOn && (
                <Stack spacing={2} alignItems="center" sx={{ zIndex: 2 }}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: "rgba(255,255,255,0.1)", color: "#94a3b8" }}>
                    <VideocamOffRoundedIcon sx={{ fontSize: 36 }} />
                  </Avatar>
                  <Typography variant="body2" fontWeight={700} color="#cbd5e1">
                    {cameraError ? cameraError : "Camera is Currently in Standby"}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<VideocamRoundedIcon />}
                    onClick={() => startCam()}
                    sx={{
                      bgcolor: activeTab === 0 ? "#059669" : "#4f46e5",
                      "&:hover": { bgcolor: activeTab === 0 ? "#047857" : "#4338ca" },
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 800,
                      px: 3,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                    }}
                  >
                    Turn Camera On
                  </Button>
                </Stack>
              )}
            </Box>

            {/* Action Bar */}
            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2.5 }}>
              <Button
                variant={cameraOn ? "outlined" : "contained"}
                color={cameraOn ? "inherit" : "primary"}
                startIcon={cameraOn ? <VideocamOffRoundedIcon /> : <VideocamRoundedIcon />}
                onClick={cameraOn ? stopCam : () => startCam()}
                sx={{
                  px: 2.5,
                  borderRadius: 3,
                  fontWeight: 800,
                  textTransform: "none",
                  borderColor: "#cbd5e1",
                }}
              >
                {cameraOn ? "Turn Camera Off" : "Start Camera"}
              </Button>

              {activeTab === 0 ? (
                <Button
                  variant="contained"
                  disabled={!memberDetails?.mem_cd || !cameraOn || loading}
                  startIcon={
                    loading ? <CircularProgress size={18} color="inherit" /> : <CameraAltRoundedIcon />
                  }
                  onClick={registerSingleFace}
                  sx={{
                    px: 4,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 900,
                    fontSize: "15px",
                    textTransform: "none",
                    background:
                      memberDetails?.mem_cd && cameraOn
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : undefined,
                    boxShadow:
                      memberDetails?.mem_cd && cameraOn
                        ? "0 8px 20px rgba(16, 185, 129, 0.35)"
                        : "none",
                    "&:hover": {
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    },
                  }}
                >
                  {loading ? "Enrolling Face..." : "Register Face"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={!cameraOn || loading}
                  startIcon={
                    loading ? <CircularProgress size={18} color="inherit" /> : <VerifiedUserRoundedIcon />
                  }
                  onClick={verifyFace}
                  sx={{
                    px: 4,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 900,
                    fontSize: "15px",
                    textTransform: "none",
                    background: cameraOn
                      ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
                      : undefined,
                    boxShadow: cameraOn
                      ? "0 8px 20px rgba(79, 70, 229, 0.35)"
                      : "none",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)",
                    },
                  }}
                >
                  {loading ? "Recognizing..." : "Verify / Scan Face"}
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* 🔍 MODAL: VIEW PARTICULAR FACE DETAILS */}
      <Dialog
        open={Boolean(previewFace)}
        onClose={() => setPreviewFace(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3.5, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#1e293b", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <VisibilityRoundedIcon sx={{ color: "#38bdf8" }} />
            <Typography variant="subtitle1" fontWeight={800}>
              Face Details — ID #{previewFace?.face_id}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setPreviewFace(null)} sx={{ color: "#94a3b8" }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
          {previewFace && (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                {/* Full Original Image */}
                <Grid item xs={12} sm={8}>
                  <Typography variant="caption" fontWeight={800} color="#475569" sx={{ display: "block", mb: 0.5 }}>
                    FULL PHOTO CAPTURED
                  </Typography>
                  <Box
                    component="img"
                    src={previewFace.image_url}
                    alt="Full Face"
                    sx={{
                      width: "100%",
                      height: 220,
                      objectFit: "cover",
                      borderRadius: 2.5,
                      border: "1px solid #cbd5e1",
                      bgcolor: "#000",
                    }}
                  />
                </Grid>

                {/* AI Cropped Face */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" fontWeight={800} color="#475569" sx={{ display: "block", mb: 0.5 }}>
                    CROPPED FACE
                  </Typography>
                  <Box
                    component="img"
                    src={previewFace.crop_url || previewFace.image_url}
                    alt="Cropped Face"
                    sx={{
                      width: "100%",
                      height: 220,
                      objectFit: "cover",
                      borderRadius: 2.5,
                      border: "2px solid #38bdf8",
                      bgcolor: "#000",
                    }}
                  />
                </Grid>
              </Grid>

              {/* Metadata details */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: "#ffffff", border: "1px solid #e2e8f0" }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>MEMBER CODE</Typography>
                    <Typography variant="body2" fontWeight={800} color="#0f172a">{previewFace.mem_cd}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>CONFIDENCE SCORE</Typography>
                    <Typography variant="body2" fontWeight={800} color="#16a34a">
                      {previewFace.confidence ? `${(previewFace.confidence * 100).toFixed(1)}%` : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>RECORD TIMESTAMP</Typography>
                    <Typography variant="body2" fontWeight={800} color="#0f172a">
                      {previewFace.created_at ? new Date(previewFace.created_at).toLocaleString() : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: "#f8fafc" }}>
          <Button
            variant="contained"
            onClick={() => setPreviewFace(null)}
            sx={{ fontWeight: 800, borderRadius: 2, textTransform: "none", px: 3 }}
          >
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION DIALOG FOR DELETE */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#b91c1c", display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteForeverRoundedIcon /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#334155" }}>
            Are you sure you want to delete <b>{selectedFaceIds.length}</b> selected face photo(s) for member{" "}
            <b>{verifyResult?.member?.mem_name}</b> ({verifyResult?.member?.mem_cd})?
            <br />
            This will permanently remove the face photo, its embedding, and associated image files.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ fontWeight: 700, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={deleteSelectedFaces}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{ fontWeight: 800, textTransform: "none", borderRadius: 2 }}
          >
            {deleteLoading ? "Deleting..." : "Yes, Delete Photos"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
